import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Play, Crown, Lock } from "lucide-react";
import { API } from "../constants";
import { useAuth } from "../context/AuthContext";
import VideoPlayerModal from "../components/VideoPlayerModal";

// Derive a YouTube thumbnail from a YouTube watch / short URL when no thumbnail_url is stored.
const ytThumbnail = (url) => {
  if (!url) return null;
  let m = url.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/) ||
          url.match(/[?&]v=([A-Za-z0-9_-]{6,})/) ||
          url.match(/youtube\.com\/embed\/([A-Za-z0-9_-]{6,})/) ||
          url.match(/youtube\.com\/shorts\/([A-Za-z0-9_-]{6,})/);
  return m ? `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg` : null;
};

const UniversityPage = () => {
  const [videos, setVideos] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);
  const { user, token } = useAuth();

  useEffect(() => {
    const fetchVideos = async () => {
      setLoading(true);
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/videos`, {
          params: { category: selectedCategory || undefined },
          headers
        });
        setVideos(response.data);
      } catch (error) {
        console.error('Failed to fetch videos');
      }
      setLoading(false);
    };
    fetchVideos();
  }, [selectedCategory, token]);

  const categories = [
    { id: '', label: 'All' },
    { id: 'polishing101', label: 'Polishing 101' },
    { id: 'beginner', label: 'Beginner' },
    { id: 'advanced', label: 'Advanced' },
    { id: 'tricks', label: 'Tricks of the Trade' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] pt-24">
      <section className="relative py-20 overflow-hidden" data-testid="university-hero">
        <div className="absolute inset-0 opacity-40">
          <img src="https://images.unsplash.com/photo-1738162837672-de9d735a9b90" alt="Polishing Sparks" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <Play className="w-16 h-16 text-[#D62828] mx-auto mb-6" />
          <h1 className="font-heading text-5xl tracking-wide mb-4">POLISHING UNIVERSITY</h1>
          <p className="text-xl text-gray-300 mb-4">Coming soon: videos by Jimmy Dandridge himself.</p>
          <p className="text-gray-400 max-w-2xl mx-auto">In the meantime, enjoy instructional videos from our suppliers. Their products work, or we wouldn't sell them.</p>
        </div>
      </section>

      <section className="py-8 bg-[#0A0A0A] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 overflow-x-auto">
          <div className="flex gap-4 min-w-max justify-center" data-testid="video-categories">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-6 py-2 rounded-sm font-semibold uppercase tracking-wide text-sm transition-colors ${selectedCategory === cat.id ? 'bg-[#D62828] text-white' : 'bg-transparent text-gray-400 hover:text-white'}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D62828] mx-auto"></div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="videos-grid">
                {videos.map((video) => {
                  const locked = video.is_vip_only && !user?.is_vip;
                  const thumb = video.thumbnail_url || ytThumbnail(video.video_url) || 'https://images.unsplash.com/photo-1738162837672-de9d735a9b90';
                  const handleClick = () => {
                    if (locked) return;
                    if (!video.video_url) return;
                    setActiveVideo(video);
                  };
                  return (
                    <button
                      key={video.video_id}
                      onClick={handleClick}
                      className="text-left bg-[#121212] border border-white/5 rounded-sm overflow-hidden group transition-transform hover:-translate-y-0.5"
                      data-testid={`video-card-${video.video_id}`}
                    >
                      <div className="relative aspect-video">
                        <img src={thumb} alt={video.title} className="w-full h-full object-cover" />
                        {locked ? (
                          <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center">
                            <Lock className="w-10 h-10 text-[#D4AF37] mb-2" />
                            <span className="text-[#D4AF37] font-semibold">VIP Only</span>
                            <Link to="/vip" className="text-sm text-gray-400 hover:text-white mt-1">Join VIP to unlock →</Link>
                          </div>
                        ) : (
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <div className="w-16 h-16 bg-[#D62828] rounded-full flex items-center justify-center">
                              <Play className="w-8 h-8 text-white ml-1" />
                            </div>
                          </div>
                        )}
                        {video.is_vip_only && (
                          <div className="absolute top-3 right-3 bg-[#D4AF37] text-black text-xs font-bold px-2 py-1 rounded-sm flex items-center gap-1">
                            <Crown className="w-3 h-3" />VIP
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">{video.category}</p>
                        <h3 className="font-semibold text-white mb-2">{video.title}</h3>
                        <div className="flex items-center justify-between text-gray-400 text-sm">
                          <span>{video.duration_minutes ? `${video.duration_minutes} min` : (video.duration || '')}</span>
                          {!locked && <span className="text-[#D62828] text-xs uppercase tracking-wide">Watch ▶</span>}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              {videos.length === 0 && !loading && (
                <div className="text-center py-16">
                  <p className="text-gray-400">No videos found</p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <VideoPlayerModal
        open={!!activeVideo}
        onClose={() => setActiveVideo(null)}
        url={activeVideo?.video_url}
        title={activeVideo?.title}
        relatedProductIds={activeVideo?.related_product_ids || []}
      />
    </div>
  );
};

export default UniversityPage;
